import { ArrowLeft, ChevronDown, GripHorizontal, GripVertical, Minus, Plus, Square, X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useScreenLogging } from '../../../hooks/useScreenLogging';
import loggingService from '../../../services/loggingService';
import { h4, sh2 } from '../../../styles/typography';
import { getAllSites } from '../../SiteManagement/siteApi';
import IconButton from '../../ui/IconButton';
import Card from '../../ui/card';
import CustomSeater from './CustomSeater';

const INPUT_BASE_CLASS = 'w-full rounded-[10px] bg-white/80 border border-gray-200 px-3 py-2 text-base text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-400/50 focus:border-green-400';

const FloorPlanDesigner = () => {
  // Log screen view
  useScreenLogging('FloorPlanDesigner', {
    component: 'FloorPlanDesigner',
    screenType: 'floor-plan',
  });

  const [sites, setSites] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [selectedSiteName, setSelectedSiteName] = useState('');
  const [floorName, setFloorName] = useState('');
  const [rows, setRows] = useState(3);
  const [columns, setColumns] = useState(3);
  const [grid, setGrid] = useState([]);
  const [isGridGenerated, setIsGridGenerated] = useState(false);
  const [isSiteDropdownOpen, setIsSiteDropdownOpen] = useState(false);
  const [zoom, setZoom] = useState(100);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [selectedSeater, setSelectedSeater] = useState(null);
  const [selectedCustomTableId, setSelectedCustomTableId] = useState(null);
  const [customTables, setCustomTables] = useState([]);
  const [showCustomSeaterOverlay, setShowCustomSeaterOverlay] = useState(false);
  const [customSeaterRows, setCustomSeaterRows] = useState(1);
  const [customSeaterColumns, setCustomSeaterColumns] = useState(1);
  const [customSeaterName, setCustomSeaterName] = useState('');
  const dragStartRef = useRef({ x: 0, y: 0 });
  const customTableIdRef = useRef(0);
  const siteDropdownRef = useRef(null);
  const gridContainerRef = useRef(null);
  const gridParentRef = useRef(null);

  // Load sites on mount
  useEffect(() => {
    const loadSites = async () => {
      try {
        const response = await getAllSites();
        const sitesData = Array.isArray(response) 
          ? response 
          : (response?.sites || response?.data || []);
        setSites(sitesData);
        // Auto-select first site if available
        if (sitesData.length > 0 && !selectedSiteId) {
          const firstSite = sitesData[0];
          setSelectedSiteId(firstSite.site_id || firstSite.id);
          setSelectedSiteName(firstSite.site_name || firstSite.name || '');
        }
        
        // Log successful API call
        loggingService.logInfo(
          'floor_plan_load_sites',
          `Successfully loaded ${sitesData.length} sites for floor plan designer`,
          {
            endpoint: '/sites',
            method: 'GET',
            siteCount: sitesData.length,
          }
        ).catch(logError => {
          console.warn('[FloorPlanDesigner] Failed to log event:', logError);
        });
      } catch (error) {
        console.error('Error loading sites:', error);
        
        // Log API error
        loggingService.logError(
          'floor_plan_load_sites_error',
          `Failed to load sites for floor plan designer: ${error.message || 'Unknown error'}`,
          error,
          {
            endpoint: '/sites',
            method: 'GET',
          }
        ).catch(logError => {
          console.warn('[FloorPlanDesigner] Failed to log error:', logError);
        });
      }
    };
    loadSites();
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (siteDropdownRef.current && !siteDropdownRef.current.contains(e.target)) {
        setIsSiteDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const generateGrid = (customRows = null, customColumns = null) => {
    const numRows = customRows !== null ? customRows : rows;
    const numColumns = customColumns !== null ? customColumns : columns;
    const newGrid = [];
    for (let i = 0; i < numRows; i++) {
      const row = [];
      for (let j = 0; j < numColumns; j++) {
        row.push({ id: `${i}-${j}`, row: i, col: j });
      }
      newGrid.push(row);
    }
    setGrid(newGrid);
  };

  const handleGenerateGrid = () => {
    if (!selectedSiteId) {
      alert('Please select a site first');
      return;
    }
    if (!floorName.trim()) {
      alert('Please enter a floor name');
      return;
    }
    generateGrid();
    setIsGridGenerated(true);
  };

  const handleBackToSetup = () => {
    setIsGridGenerated(false);
    setSelectedSeater(null);
    setSelectedCustomTableId(null);
    setCustomTables([]);
    setShowCustomSeaterOverlay(false);
    setCustomSeaterRows(1);
    setCustomSeaterColumns(1);
    setCustomSeaterName('');
  };

  const handleConfirmLayout = () => {
    const selectedCustomTable = selectedCustomTableId
      ? customTables.find((t) => t.id === selectedCustomTableId)
      : null;
    console.log('Confirming layout with:', {
      site: selectedSiteName,
      floor: floorName,
      rows,
      columns,
      selectedSeater,
      selectedCustomTable,
      customTables
    });
    // You can add API call or further processing here
  };

  const handleAddRow = (position) => {
    const newRows = rows + 1;
    setRows(newRows);
    // Regenerate grid with new row count
    const newGrid = [];
    for (let i = 0; i < newRows; i++) {
      const row = [];
      for (let j = 0; j < columns; j++) {
        row.push({ id: `${i}-${j}`, row: i, col: j });
      }
      newGrid.push(row);
    }
    setGrid(newGrid);
  };

  const handleAddColumn = (position) => {
    const newColumns = columns + 1;
    setColumns(newColumns);
    // Regenerate grid with new column count
    const newGrid = [];
    for (let i = 0; i < rows; i++) {
      const row = [];
      for (let j = 0; j < newColumns; j++) {
        row.push({ id: `${i}-${j}`, row: i, col: j });
      }
      newGrid.push(row);
    }
    setGrid(newGrid);
  };

  const handleRemoveRow = () => {
    if (rows > 1) {
      const newRows = rows - 1;
      setRows(newRows);
      // Regenerate grid with new row count
      const newGrid = [];
      for (let i = 0; i < newRows; i++) {
        const row = [];
        for (let j = 0; j < columns; j++) {
          row.push({ id: `${i}-${j}`, row: i, col: j });
        }
        newGrid.push(row);
      }
      setGrid(newGrid);
    }
  };

  const handleRemoveColumn = () => {
    if (columns > 1) {
      const newColumns = columns - 1;
      setColumns(newColumns);
      // Regenerate grid with new column count
      const newGrid = [];
      for (let i = 0; i < rows; i++) {
        const row = [];
        for (let j = 0; j < newColumns; j++) {
          row.push({ id: `${i}-${j}`, row: i, col: j });
        }
        newGrid.push(row);
      }
      setGrid(newGrid);
    }
  };

  const handleZoomIn = () => {
    setZoom(Math.min(zoom + 10, 200));
  };

  const handleZoomOut = () => {
    setZoom(Math.max(zoom - 10, 50));
  };

  const handleMouseDown = (e) => {
    // Don't start dragging if clicking on buttons, zoom controls, or grid cells
    const target = e.target;
    if (
      target.closest('button') || 
      target.closest('.grid-cell') ||
      target.closest('[aria-label*="Zoom"]') ||
      target.tagName === 'BUTTON'
    ) {
      return;
    }
    
    // Only allow dragging if clicking on the parent container or empty space
    if (isGridGenerated && grid.length > 0) {
      e.preventDefault();
      setIsDragging(true);
      dragStartRef.current = {
        x: e.clientX - position.x,
        y: e.clientY - position.y
      };
    }
  };

  // Add global mouse event listeners for dragging
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e) => {
      setPosition({
        x: e.clientX - dragStartRef.current.x,
        y: e.clientY - dragStartRef.current.y
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);


  const panelStyle = {
    background: 'linear-gradient(0deg, rgba(255, 255, 255, 0.10) 0%, rgba(255, 255, 255, 0.10) 100%), linear-gradient(170deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.40) 97.94%)',
    boxShadow: '0px 5px 20.1px 0px rgba(255, 255, 255, 0.10), 0px 15px 30px 0px rgba(0, 0, 0, 0.10), 0px 4px 25px 0px rgba(255, 255, 255, 0.35) inset',
    border: '1px solid rgba(255, 255, 255, 0.5)',
    backdropFilter: 'blur(30px)',
  };

  return (
    <div className="flex w-full h-full gap-4">
      {/* Left Panel - Controls */}
      <Card
        className="w-full max-w-[320px] flex flex-col rounded-xl overflow-hidden flex-shrink-0"
        style={panelStyle}
      >
        {!isGridGenerated ? (
          <div className="p-4 flex flex-col gap-4 flex-1">
            {/* Title */}
            <h4 className={`${h4} text-gray-800 font-semibold`}>Floor design plan</h4>

            {/* Select site */}
            <div>
              <label className={`block ${sh2} text-gray-700 mb-2`}>Select site</label>
              <div className="relative" ref={siteDropdownRef}>
                <button
                  type="button"
                  onClick={() => setIsSiteDropdownOpen(!isSiteDropdownOpen)}
                  className="w-full h-11 pl-4 pr-3 flex items-center justify-between rounded-[10px] text-left text-gray-700 bg-white/80 border border-gray-200 shadow-sm hover:bg-white/90 transition-colors"
                >
                  <span className="text-base font-normal truncate">
                    {selectedSiteName || 'Select site'}
                  </span>
                  <ChevronDown
                    className={`w-5 h-5 text-gray-500 flex-shrink-0 transition-transform ${isSiteDropdownOpen ? 'rotate-180' : ''}`}
                    strokeWidth={2}
                  />
                </button>
                {isSiteDropdownOpen && (
                  <div className="absolute top-full left-0 right-0 mt-1 py-1 bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-60 overflow-y-auto">
                    {sites.length === 0 ? (
                      <div className="px-4 py-2.5 text-gray-500 text-sm">No sites available</div>
                    ) : (
                      sites.map((site) => (
                        <button
                          key={site.site_id || site.id}
                          type="button"
                          onClick={() => {
                            setSelectedSiteId(site.site_id || site.id);
                            setSelectedSiteName(site.site_name || site.name || '');
                            setIsSiteDropdownOpen(false);
                          }}
                          className="w-full text-left px-4 py-2.5 text-gray-700 hover:bg-gray-100 text-sm"
                        >
                          {site.site_name || site.name || 'Unknown Site'}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Floor name */}
            <div>
              <label className={`block ${sh2} text-gray-700 mb-2`}>Floor name</label>
              <input
                type="text"
                value={floorName}
                onChange={(e) => setFloorName(e.target.value)}
                placeholder="Enter floor name"
                className={INPUT_BASE_CLASS}
              />
            </div>

            {/* Select row and column */}
            <div>
              <label className={`block ${sh2} text-gray-700 mb-2`}>Select row and colum</label>
              <div className="flex items-center gap-2">
                {/* Row input */}
                <div className="relative flex-1">
                  <div className="relative">
                    <GripHorizontal className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" strokeWidth={2} />
                    <input
                      type="number"
                      min="1"
                      max="20"
                      value={rows}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 1;
                        if (value >= 1 && value <= 20) {
                          setRows(value);
                        }
                      }}
                      className={`${INPUT_BASE_CLASS} pl-10 pr-3 h-11`}
                    />
                  </div>
                </div>

                <span className="text-gray-400 text-lg">~</span>

                {/* Column input */}
                <div className="relative flex-1">
                  <div className="relative">
                    <GripVertical className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" strokeWidth={2} />
                    <input
                      type="number"
                      min="1"
                      max="20"
                      value={columns}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 1;
                        if (value >= 1 && value <= 20) {
                          setColumns(value);
                        }
                      }}
                      className={`${INPUT_BASE_CLASS} pl-10 pr-3 h-11`}
                    />
                  </div>
                </div>
              </div>
              {/* Generate grid button */}
              <button
                type="button"
                onClick={handleGenerateGrid}
                className="w-full py-3 px-4 rounded-[10px] bg-green-600 hover:bg-green-700 text-white font-medium text-base transition-colors shadow-md mt-6"
              >
                Generate grid and plan tables
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col h-full">
            {/* Header Section */}
            <div className="bg-white/30 backdrop-blur-sm rounded-t-xl p-4 border-b border-white/20">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleBackToSetup}
                  className="w-8 h-8 rounded-full bg-white/80 hover:bg-white flex items-center justify-center transition-colors shadow-sm"
                >
                  <ArrowLeft className="w-4 h-4 text-gray-700" strokeWidth={2} />
                </button>
                <h4 className={`${h4} text-gray-800 font-semibold flex-1 text-center`}>Floor design plan</h4>
              </div>
            </div>

            {/* Content Section */}
            <div className="p-4 flex flex-col gap-6 flex-1 overflow-y-auto">
              {/* Site and Floor Info */}
              <div className="space-y-2">
                <div className="text-sm text-gray-700">
                  <span className="font-medium">Site: </span>
                  <span>{selectedSiteName || 'xyz'}</span>
                </div>
                <div className="text-sm text-gray-700">
                  <span className="font-medium">Floor: </span>
                  <span>{floorName || 'INDOOR'}</span>
                </div>
              </div>

              {/* Select row and column */}
              <div>
                <h3 className={`${sh2} text-gray-800 font-semibold mb-3`}>Select row and colum</h3>
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <div className="relative">
                      <GripHorizontal className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" strokeWidth={2} />
                      <input
                        type="number"
                        min="1"
                        max="20"
                        value={rows}
                        onChange={(e) => {
                          const value = parseInt(e.target.value) || 1;
                          if (value >= 1 && value <= 20) {
                            setRows(value);
                            generateGrid(value, columns);
                          }
                        }}
                        className={`${INPUT_BASE_CLASS} pl-10 pr-3 h-11`}
                      />
                    </div>
                  </div>
                  <span className="text-gray-400 text-lg">~</span>
                  <div className="relative flex-1">
                    <div className="relative">
                      <GripVertical className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" strokeWidth={2} />
                      <input
                        type="number"
                        min="1"
                        max="20"
                        value={columns}
                        onChange={(e) => {
                          const value = parseInt(e.target.value) || 1;
                          if (value >= 1 && value <= 20) {
                            setColumns(value);
                            generateGrid(rows, value);
                          }
                        }}
                        className={`${INPUT_BASE_CLASS} pl-10 pr-3 h-11`}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Select seater */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className={`${sh2} text-gray-800 font-semibold`}>Select seater</h3>
                  <IconButton
                    onClick={() => setShowCustomSeaterOverlay(true)}
                    icon={<Plus className="w-4 h-4 text-white" strokeWidth={2} />}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-[10px] bg-green-600 hover:bg-green-700 text-white text-sm font-medium transition-colors shadow-sm w-auto h-auto"
                    variant="success"
                  >
                    <span>Table</span>
                  </IconButton>
                </div>
                <div className="grid grid-cols-4 gap-2">
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((seater) => (
                    <button
                      key={seater}
                      type="button"
                      onClick={() => {
                        setSelectedSeater(seater);
                        setSelectedCustomTableId(null);
                      }}
                      className={`p-3 rounded-[10px] border-2 transition-all flex flex-col items-center gap-2 ${
                        selectedSeater === seater && !selectedCustomTableId
                          ? 'bg-green-100 border-green-500 shadow-md'
                          : 'bg-white/80 border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="relative w-12 h-12 flex items-center justify-center">
                        <div className="relative">
                          <Square className="w-10 h-10 text-gray-600" strokeWidth={1.5} fill="rgba(156, 163, 175, 0.2)" />
                          <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gray-500 rounded-full"></div>
                          <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gray-500 rounded-full"></div>
                          <div className="absolute -left-1 top-1/2 transform -translate-y-1/2 w-2 h-2 bg-gray-500 rounded-full"></div>
                          <div className="absolute -right-1 top-1/2 transform -translate-y-1/2 w-2 h-2 bg-gray-500 rounded-full"></div>
                          <span className="absolute inset-0 flex items-center justify-center text-[10px] font-semibold text-gray-700">
                            T-01
                          </span>
                        </div>
                      </div>
                      <span className="text-xs font-medium text-gray-700">{seater} seater</span>
                    </button>
                  ))}
                  {customTables.map((table) => (
                    <button
                      key={table.id}
                      type="button"
                      onClick={() => {
                        setSelectedCustomTableId(table.id);
                        setSelectedSeater(null);
                      }}
                      className={`p-3 rounded-[10px] border-2 transition-all flex flex-col items-center gap-2 ${
                        selectedCustomTableId === table.id
                          ? 'bg-green-100 border-green-500 shadow-md'
                          : 'bg-white/80 border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="relative w-12 h-12 flex items-center justify-center">
                        <div className="relative">
                          <Square className="w-10 h-10 text-gray-600" strokeWidth={1.5} fill="rgba(156, 163, 175, 0.2)" />
                          <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gray-500 rounded-full"></div>
                          <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gray-500 rounded-full"></div>
                          <div className="absolute -left-1 top-1/2 transform -translate-y-1/2 w-2 h-2 bg-gray-500 rounded-full"></div>
                          <div className="absolute -right-1 top-1/2 transform -translate-y-1/2 w-2 h-2 bg-gray-500 rounded-full"></div>
                          <span className="absolute inset-0 flex items-center justify-center text-[10px] font-semibold text-gray-700">
                            {table.name}
                          </span>
                        </div>
                      </div>
                      <span className="text-xs font-medium text-gray-700">{table.rows}×{table.columns}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* CustomSeater overlay - shown when "+ Table" is clicked */}
              {showCustomSeaterOverlay && (
                <div
                  className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
                  onClick={() => {
                    setShowCustomSeaterOverlay(false);
                    setCustomSeaterName('');
                    setCustomSeaterRows(1);
                    setCustomSeaterColumns(1);
                  }}
                >
                  <div
                    className="bg-white rounded-xl shadow-xl max-w-md w-full overflow-hidden"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="border-b border-gray-200 flex items-center justify-end p-2">
                      <button
                        type="button"
                        onClick={() => {
                          setShowCustomSeaterOverlay(false);
                          setCustomSeaterName('');
                          setCustomSeaterRows(1);
                          setCustomSeaterColumns(1);
                        }}
                        className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-600"
                        aria-label="Close"
                      >
                        <X className="w-5 h-5" strokeWidth={2} />
                      </button>
                    </div>
                    <div className="p-4">
                      <CustomSeater
                        customSeaterName={customSeaterName}
                        setCustomSeaterName={setCustomSeaterName}
                        customSeaterRows={customSeaterRows}
                        setCustomSeaterRows={setCustomSeaterRows}
                        customSeaterColumns={customSeaterColumns}
                        setCustomSeaterColumns={setCustomSeaterColumns}
                        onAddTable={(customSeater) => {
                          const id = `custom-${++customTableIdRef.current}`;
                          setCustomTables((prev) => [...prev, { id, ...customSeater }]);
                          setCustomSeaterName('');
                          setCustomSeaterRows(1);
                          setCustomSeaterColumns(1);
                          setShowCustomSeaterOverlay(false);
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Confirm Layout Button */}
              <button
                type="button"
                onClick={handleConfirmLayout}
                className="w-full py-3 px-4 rounded-[10px] bg-green-600 hover:bg-green-700 text-white font-semibold text-base transition-colors shadow-md mt-auto"
              >
                Confirm Layout
              </button>
            </div>
          </div>
        )}
      </Card>

      {/* Right Panel - Grid Editor */}
      <Card
        className="flex-1 min-w-0 flex flex-col rounded-xl overflow-hidden relative"
        style={panelStyle}
      >
        <div 
          ref={gridParentRef}
          className="flex-1 p-6 flex flex-col items-center justify-center relative overflow-hidden"
          style={{ cursor: isGridGenerated && grid.length > 0 && !isDragging ? 'grab' : isDragging ? 'grabbing' : 'default' }}
          onMouseDown={handleMouseDown}
        >
          {/* Zoom Controls */}
          <div className="absolute top-4 right-4 z-10">
            <div className="bg-white/90 rounded-lg border border-gray-200 shadow-sm flex flex-col">
              <button
                type="button"
                onClick={handleZoomIn}
                className="p-2 hover:bg-gray-100 transition-colors rounded-t-lg"
                aria-label="Zoom in"
              >
                <Plus className="w-4 h-4 text-gray-600" strokeWidth={2} />
              </button>
              <div className="border-t border-gray-200"></div>
              <button
                type="button"
                onClick={handleZoomOut}
                className="p-2 hover:bg-gray-100 transition-colors rounded-b-lg"
                aria-label="Zoom out"
              >
                <Minus className="w-4 h-4 text-gray-600" strokeWidth={2} />
              </button>
            </div>
          </div>

          {/* Grid Container */}
          {isGridGenerated && grid.length > 0 ? (
            <div
              ref={gridContainerRef}
              className="grid-container flex flex-col items-center justify-center gap-2 select-none pointer-events-none"
              style={{ 
                transform: `translate(${position.x}px, ${position.y}px) scale(${zoom / 100})`, 
                transformOrigin: 'center'
              }}
            >
              {/* Row Control Button - Top */}
              <div className="flex items-center pointer-events-auto">
                <div className="flex items-center rounded-lg bg-green-600 hover:bg-green-700 shadow-lg transition-colors overflow-hidden">
                  <button
                    type="button"
                    onClick={() => handleAddRow('top')}
                    className="px-3 py-2 text-white flex items-center justify-center hover:bg-green-800 transition-colors"
                    aria-label="Add row above"
                  >
                    <Plus className="w-4 h-4" strokeWidth={2.5} />
                  </button>
                  <div className="w-px h-6 bg-green-500"></div>
                  <button
                    type="button"
                    onClick={handleRemoveRow}
                    disabled={rows <= 1}
                    className="px-3 py-2 text-white flex items-center justify-center hover:bg-green-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                    aria-label="Remove row"
                  >
                    <Minus className="w-4 h-4" strokeWidth={2.5} />
                  </button>
                </div>
              </div>

              {/* Grid and Side Buttons Container */}
              <div className="flex items-center gap-2">
                {/* Column Control Buttons - Left */}
                <div className="flex flex-col items-center pointer-events-auto">
                  <div className="flex flex-col items-center rounded-lg bg-green-600 hover:bg-green-700 shadow-lg transition-colors overflow-hidden">
                    <button
                      type="button"
                      onClick={() => handleAddColumn('left')}
                      className="px-3 py-2 text-white flex items-center justify-center hover:bg-green-800 transition-colors"
                      aria-label="Add column left"
                    >
                      <Plus className="w-4 h-4" strokeWidth={2.5} />
                    </button>
                    <div className="h-px w-6 bg-green-500"></div>
                    <button
                      type="button"
                      onClick={handleRemoveColumn}
                      disabled={columns <= 1}
                      className="px-3 py-2 text-white flex items-center justify-center hover:bg-green-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                      aria-label="Remove column"
                    >
                      <Minus className="w-4 h-4" strokeWidth={2.5} />
                    </button>
                  </div>
                </div>

                {/* Grid */}
                <div className="flex flex-col gap-1 pointer-events-auto">
                  {grid.map((row, rowIndex) => (
                    <div key={rowIndex} className="flex gap-1">
                      {row.map((cell) => (
                        <div
                          key={cell.id}
                          className="grid-cell w-16 h-16 bg-green-100 border-2 border-green-300 rounded-sm flex items-center justify-center cursor-pointer hover:bg-green-200 transition-colors"
                          style={{ minWidth: '64px', minHeight: '64px' }}
                        >
                          {/* Optional: Add cell content or table indicators here */}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>

                {/* Column Control Buttons - Right */}
                <div className="flex flex-col items-center pointer-events-auto">
                  <div className="flex flex-col items-center rounded-lg bg-green-600 hover:bg-green-700 shadow-lg transition-colors overflow-hidden">
                    <button
                      type="button"
                      onClick={() => handleAddColumn('right')}
                      className="px-3 py-2 text-white flex items-center justify-center hover:bg-green-800 transition-colors"
                      aria-label="Add column right"
                    >
                      <Plus className="w-4 h-4" strokeWidth={2.5} />
                    </button>
                    <div className="h-px w-6 bg-green-500"></div>
                    <button
                      type="button"
                      onClick={handleRemoveColumn}
                      disabled={columns <= 1}
                      className="px-3 py-2 text-white flex items-center justify-center hover:bg-green-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                      aria-label="Remove column"
                    >
                      <Minus className="w-4 h-4" strokeWidth={2.5} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Row Control Button - Bottom */}
              <div className="flex items-center pointer-events-auto">
                <div className="flex items-center rounded-lg bg-green-600 hover:bg-green-700 shadow-lg transition-colors overflow-hidden">
                  <button
                    type="button"
                    onClick={() => handleAddRow('bottom')}
                    className="px-3 py-2 text-white flex items-center justify-center hover:bg-green-800 transition-colors"
                    aria-label="Add row below"
                  >
                    <Plus className="w-4 h-4" strokeWidth={2.5} />
                  </button>
                  <div className="w-px h-6 bg-green-500"></div>
                  <button
                    type="button"
                    onClick={handleRemoveRow}
                    disabled={rows <= 1}
                    className="px-3 py-2 text-white flex items-center justify-center hover:bg-green-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                    aria-label="Remove row"
                  >
                    <Minus className="w-4 h-4" strokeWidth={2.5} />
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center text-gray-400">
              <p className="text-lg font-medium">No grid generated yet</p>
              <p className="text-sm mt-2">Select rows and columns, then click "Generate grid" to create your floor plan</p>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default FloorPlanDesigner;
